import { useEffect, useState } from "react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import FinalCta from "@/components/FinalCta";
import { LESSONS } from "@/lib/learn";

export default function Learn() {
  const [saveProgress, setSaveProgress] = useState(false);
  const [done, setDone] = useState([]);

  useEffect(() => {
    if (saveProgress) {
      try {
        const saved = JSON.parse(localStorage.getItem("sbb-belt-progress") || "[]");
        setDone(Array.isArray(saved) ? saved : []);
      } catch (e) {}
    }
  }, [saveProgress]);

  function toggleSave() {
    if (saveProgress) {
      setSaveProgress(false);
      setDone([]);
    } else {
      setSaveProgress(true);
    }
  }

  function markDone(i) {
    if (!saveProgress) return;
    setDone((d) => {
      const next = d.includes(i) ? d.filter((x) => x !== i) : [...d, i];
      try {
        localStorage.setItem("sbb-belt-progress", JSON.stringify(next));
      } catch (e) {}
      return next;
    });
  }

  return (
    <>
      <Seo
        title="Learn solar | The Solar Black Belt path"
        description="Five self-paced lessons from your first kilowatt to a confident project conversation: know your bill, understand your roof, connect solar and storage, compare the economics, plan your next move."
      />
      <section className="page-hero">
        <div className="wrap">
          <span className="eyebrow">THE SOLAR BLACK BELT PATH</span>
          <h1 data-testid="learn-heading">Five self-paced lessons. Zero pressure.</h1>
          <p className="lead">
            Learning here is a courtesy, not a sales funnel. Read one lesson or all five, in any order, at your pace — nothing is
            gated, nothing counts down, and the path stays exactly where you left it. Progression is educational, not a
            professional certification.
          </p>
        </div>
      </section>
      <section className="section" style={{ paddingTop: 44 }}>
        <div className="wrap">
          <div className="belt-progress" data-testid="learn-progress">
            <label className="check" style={{ margin: 0 }}>
              <input type="checkbox" checked={saveProgress} onChange={toggleSave} data-testid="learn-save-progress" />
              <span>Save my progress in this browser</span>
            </label>
            {saveProgress && (
              <>
                <div className="bar" role="progressbar" aria-valuenow={done.length} aria-valuemin={0} aria-valuemax={LESSONS.length}>
                  <i style={{ width: `${(done.length / LESSONS.length) * 100}%` }} />
                </div>
                <b>{done.length} / {LESSONS.length}</b>
              </>
            )}
          </div>
          {LESSONS.map((l, i) => (
            <Reveal key={l.name} delay={i * 0.04}>
              <article
                style={{ background: "white", border: "1px solid var(--line)", borderRadius: 16, padding: "30px 28px", marginBottom: 18 }}
                data-testid={`learn-lesson-${i + 1}`}
              >
                <header style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 8 }}>
                  <span className="eyebrow">{l.name}</span>
                  <h2 style={{ fontSize: 26, margin: 0 }}>{l.title}</h2>
                  <span className="report-tag">{l.minutes}</span>
                  {saveProgress && (
                    <button className={`chip ${done.includes(i) ? "active" : ""}`} data-testid={`learn-done-${i + 1}`} onClick={() => markDone(i)} aria-pressed={done.includes(i)}>
                      {done.includes(i) ? "✓ Completed" : "Mark complete"}
                    </button>
                  )}
                </header>
                <p style={{ marginTop: 12, maxWidth: 760, color: "var(--muted)", fontSize: 14 }}>{l.intro}</p>
                {l.sections.map((s) => (
                  <section key={s.h} style={{ marginTop: 18 }}>
                    <h3 style={{ fontSize: 17, margin: "0 0 6px" }}>{s.h}</h3>
                    {s.p.map((para, j) => (
                      <p key={j} style={{ maxWidth: 760, color: "var(--muted)", fontSize: 14, lineHeight: 1.75, margin: "0 0 10px" }}>{para}</p>
                    ))}
                  </section>
                ))}
                <div style={{ display: "flex", gap: 26, flexWrap: "wrap", marginTop: 16 }}>
                  <div style={{ flex: "1 1 280px" }}>
                    <h3 style={{ fontSize: 15, margin: "0 0 6px" }}>Key terms</h3>
                    <dl style={{ margin: 0 }}>
                      {l.terms.map(([t, d]) => (
                        <div key={t} style={{ marginBottom: 6 }}>
                          <dt style={{ fontWeight: 800, fontSize: 13, display: "inline" }}>{t}</dt>
                          <dd style={{ display: "inline", margin: "0 0 0 6px", fontSize: 13, color: "var(--muted)" }}>{d}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                  <div style={{ flex: "1 1 280px" }}>
                    <h3 style={{ fontSize: 15, margin: "0 0 6px" }}>Try this</h3>
                    <ul className="benefitlist" style={{ margin: 0 }}>
                      {l.tryThis.map((n) => (
                        <li key={n}>{n}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <p className="micro" style={{ marginTop: 16, borderTop: "1px solid var(--line)", paddingTop: 12 }}>{l.nextStep}</p>
              </article>
            </Reveal>
          ))}
          <p className="micro" data-testid="learn-disclaimer">
            The Solar Black Belt rank is an internal engagement milestone, not a professional certification. Progress, when saved, stays in this browser only.
          </p>
        </div>
      </section>
      <FinalCta title={["Take the next step"]} service="commercial" context="Quote request" />
    </>
  );
}
